<template>
    <div
        class="legacy-content-items"
    >
        <DebugContent
            title="DEBUG: Legacy Content Items"
            class="ma-8"
        >
            <template #expand>
                <ul>
                    <li v-for="(item, index) in contentTypes" :key="index">
                        <span>{{ item }}</span>
                    </li>
                </ul>
            </template>
        </DebugContent>

        <template
            v-for="(group, groupIndex) in contentGroups"
            :key="`content-group-${groupIndex}`"
        >
            <ContentBlocks
                v-if="group.kind === 'legacy'"
                :blocks="group.blocks"
                class="d-flex fill-width fill-height"
            ></ContentBlocks>
            <template
                v-else
            >
                <BlockRenderer
                    v-for="(block, blockIndex) in group.blocks"
                    :key="`content-group-${groupIndex}-block-${blockIndex}`"
                    :model-value="block"
                ></BlockRenderer>
            </template>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import type {
        LegacyContentItem,
        ContentBlock,
        ContentBlockRendered,
        ProductFeatureContentBlockItem
    } from '~/types';
    import { LayoutTypeForFeatures } from '~/types';
    import { useLegacyContent } from '~/composables';
    import {
        useCdnResource
    } from 'x1-base/composables';

    const ContentBlocks = defineAsyncComponent(() => import('~/components/content/ContentBlocks/ContentBlocks.vue'));
    const BlockRenderer = defineAsyncComponent(() => import('~/components/content/blocks/v2/BlockRenderer/BlockRenderer.vue'));
    const DebugContent = defineAsyncComponent(() => import('~/components/DebugContent/DebugContent.vue'));

    type ContentGroup =
        | { kind: 'legacy'; blocks: ContentBlock[] }
        | { kind: 'rendered'; blocks: ContentBlockRendered[] };

    const props = defineProps<{
        items: (LegacyContentItem | ProductFeatureContentBlockItem)[];
        contentBlocks?: ContentBlockRendered[];
    }>();

    const { cdnBasePathSkCms } = useCdnResource();
    const { getFeatureBlockObj } = useLegacyContent({
        cdnBasePath: cdnBasePathSkCms
    });
    const contentTypes = ref<string[]>([]);
    const contentGroups = ref<ContentGroup[]>([]);

    function isContentBlockItem(item: LegacyContentItem | ProductFeatureContentBlockItem): item is ProductFeatureContentBlockItem {
        return 'ref' in item;
    }

    // Consecutive legacy blocks share one ContentBlocks wrapper so half-width
    // blocks keep pairing up into rows.
    function pushLegacyBlock(block: ContentBlock): void {
        const lastGroup = contentGroups.value[contentGroups.value.length - 1];

        if (lastGroup?.kind === 'legacy') {
            lastGroup.blocks.push(block);
        } else {
            contentGroups.value.push({ kind: 'legacy', blocks: [block] });
        }
    }

    function pushRenderedBlock(block: ContentBlockRendered): void {
        const lastGroup = contentGroups.value[contentGroups.value.length - 1];

        if (lastGroup?.kind === 'rendered') {
            lastGroup.blocks.push(block);
        } else {
            contentGroups.value.push({ kind: 'rendered', blocks: [block] });
        }
    }

    if (props.items) {
        props.items.forEach((item, index) => {
            const isFirst = index === 0;
            const isLast = index === props.items.length - 1;

            if (isContentBlockItem(item)) {
                const renderedBlock = props.contentBlocks?.find((contentBlock) => contentBlock.ref === item.ref);

                if (renderedBlock) {
                    contentTypes.value.push(`(${item.ref}) CONTENT BLOCK: Rendered via BlockRenderer`);
                    pushRenderedBlock(renderedBlock);
                } else {
                    contentTypes.value.push(`(${item.ref}) CONTENT BLOCK: No matching content block found`);
                }

                return;
            }

            let block: ContentBlock | null = null;

            // First Try to use the new layoutType property if it exists
            if (item.layoutType) {
                switch (item.layoutType) {
                    case LayoutTypeForFeatures.HALF:
                        block = createHalf(index, item, isFirst, isLast);
                        break;
                    case LayoutTypeForFeatures.HORIZONTAL:
                        block = createHorizontal(index, item, isFirst, isLast);
                        break;
                    case LayoutTypeForFeatures.VERTICAL:
                        block = createVertical(index, item, isFirst, isLast);
                        break;
                    case LayoutTypeForFeatures.STACKED:
                        block = createStacked(index, item, isFirst, isLast);
                        break;
                }
            } else {
                // Fallback to type detection
                if (item.width === 'half') {
                    // Half Width / Text w/Background
                    block = createHalf(index, item, isFirst, isLast);
                } else if (item.textPosX.match(/left|right/) && item.imagePosX.match(/left|right/)) {
                    // Full Width / Text & Image / Side-by-Side
                    block = createHorizontal(index, item, isFirst, isLast);
                } else if (
                    (item.textPosY === 'top' &&
                        (item.imagePosY === 'middle' || item.imagePosY === 'bottom') &&
                        item.imagePosX !== 'center') ||
                    (item.imagePosY === 'top' &&
                        (item.textPosY === 'middle' || item.textPosY === 'bottom') &&
                        item.imagePosX !== 'center')
                ) {
                    // Full Width / Text & Image / Top-to-Bottom
                    block = createVertical(index, item, isFirst, isLast);
                } else {
                    // item.layoutType === 'stacked'
                    // Full Width / Text Absolute Positioned w/Background
                    block = createStacked(index, item, isFirst, isLast);
                }
            }

            if (block) {
                pushLegacyBlock(block);
            }
        });

        function createHalf(index: number, item: LegacyContentItem, isFirst: boolean, isLast: boolean): ContentBlock | null {
            const prevItem = props.items[index - 1];
            const nextItem = props.items[index + 1];

            const isFirstInRow = (prevItem && (isContentBlockItem(prevItem) || prevItem.width !== 'half')) ?? false;
            const isLastInRow = (nextItem && (isContentBlockItem(nextItem) || nextItem.width !== 'half')) ?? false;

            contentTypes.value.push(`(${item.id}) HALF: Half Width / Text w/Background (FIRST: ${isFirstInRow} | LAST: ${isLastInRow})`);
            return getFeatureBlockObj(LayoutTypeForFeatures.HALF, item, isFirst, isLast, isFirstInRow, isLastInRow);
        }

        function createHorizontal(index: number, item: LegacyContentItem, isFirst: boolean, isLast: boolean): ContentBlock | null {
            contentTypes.value.push(`(${item.id}) HORIZONTAL: Full Width / Text & Image / Side-by-Side`);
            return getFeatureBlockObj(LayoutTypeForFeatures.HORIZONTAL, item, isFirst, isLast);
        }

        function createVertical(index: number, item: LegacyContentItem, isFirst: boolean, isLast: boolean): ContentBlock | null {
            contentTypes.value.push(`(${item.id}) VERTICAL: Full Width / Text & Image / Top-to-Bottom`);
            return getFeatureBlockObj(LayoutTypeForFeatures.VERTICAL, item, isFirst, isLast);
        }

        function createStacked(index: number, item: LegacyContentItem, isFirst: boolean, isLast: boolean): ContentBlock | null {
            contentTypes.value.push(`(${item.id}) STACKED: Full Width / Text Absolute Positioned w/Background`);
            return getFeatureBlockObj(LayoutTypeForFeatures.STACKED, item, isFirst, isLast);
        }
    }
</script>

<style lang="scss" scoped></style>
